import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Trash2, MessageCircle, ExternalLink } from 'lucide-react';
import { TICKET_STATUSES, TICKET_PRIORITIES } from '@superchat/shared/constants';
import { notificationRoute } from '@superchat/shared/notifications';
import { api } from '../lib/api.js';
import { clockTime, STATUS_LABEL, PRIORITY_LABEL } from '../lib/format.js';
import TicketAttachments from './TicketAttachments.jsx';

/**
 * One ticket, in full.
 *
 * The table could only ever change the three fields it had room for — status,
 * priority, assignee — so a subject typo was permanent, a description could be
 * written once and never read back, and tags and the linked conversation were
 * settable at creation and invisible afterwards. Everything the API has always
 * accepted is editable here.
 *
 * Saved as one patch rather than field by field: a ticket is a description of
 * a problem, and half-applying an edit to it — the new subject saved, the
 * matching description lost to a failed request — is worse than refusing the
 * lot. Only what actually changed is sent, so two agents editing different
 * fields do not overwrite each other.
 */
export function TicketDialog({ appId, ticket, members, conversations, uploads, onClose, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(ticket);
  const [tagDraft, setTagDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /* Another agent's edit to this same ticket arrives through the list. */
  useEffect(() => {
    setDraft((current) => (current.id === ticket.id ? current : ticket));
  }, [ticket]);

  const set = (patch) => setDraft((current) => ({ ...current, ...patch }));

  /** Exactly what moved, so an untouched field is never sent. */
  const changes = useMemo(() => {
    const diff = {};
    for (const key of ['subject', 'description', 'status', 'priority']) {
      if (draft[key] !== ticket[key]) diff[key] = draft[key];
    }
    if ((draft.assigneeId ?? null) !== (ticket.assigneeId ?? null)) {
      diff.assigneeId = draft.assigneeId || null;
    }
    if ((draft.conversationId ?? null) !== (ticket.conversationId ?? null)) {
      diff.conversationId = draft.conversationId || null;
    }
    if (draft.tags.join(' ') !== ticket.tags.join(' ')) diff.tags = draft.tags;
    return diff;
  }, [draft, ticket]);

  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.tickets.update(appId, ticket.id, changes);
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete SC-${ticket.number}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.tickets.remove(appId, ticket.id);
      onDeleted(ticket);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const addTag = (event) => {
    event.preventDefault();
    const tag = tagDraft.trim();
    if (!tag || draft.tags.includes(tag)) return;
    set({ tags: [...draft.tags, tag] });
    setTagDraft('');
  };

  const nameOf = (userId) => {
    const member = members.find((m) => m.userId === userId);
    return member ? member.name || member.email : null;
  };

  const linked = conversations.find((c) => c.id === draft.conversationId);
  const closedAt = ticket.closedAt ?? ticket.resolvedAt;

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog dialog--wide"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Ticket SC-${ticket.number}`}
      >
        <header className="dialog__head">
          <h2>
            <span className="mono muted">SC-{ticket.number}</span> {ticket.subject}
          </h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <label className="field">
          <span className="field__label">SUBJECT</span>
          <input
            className="field__control"
            maxLength={300}
            value={draft.subject}
            onChange={(e) => set({ subject: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">DESCRIPTION</span>
          <textarea
            className="field__control field__control--area"
            rows={6}
            maxLength={20000}
            value={draft.description}
            placeholder="What is the problem, and what has been tried?"
            onChange={(e) => set({ description: e.target.value })}
          />
        </label>

        <div className="dialog__grid">
          <label className="field">
            <span className="field__label">STATUS</span>
            <select
              className="field__control"
              value={draft.status}
              onChange={(e) => set({ status: e.target.value })}
            >
              {TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">PRIORITY</span>
            <select
              className="field__control"
              value={draft.priority}
              onChange={(e) => set({ priority: e.target.value })}
            >
              {TICKET_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">ASSIGNEE</span>
            <select
              className="field__control"
              value={draft.assigneeId ?? ''}
              onChange={(e) => set({ assigneeId: e.target.value || null })}
            >
              <option value="">Unassigned</option>
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name || member.email}
                  {member.isOwner ? ' · owner' : ''}
                </option>
              ))}
              {/* Assigned to someone since removed from the app; without this
                  the select falls back to the first option and reads as
                  unassigned, which it is not. */}
              {draft.assigneeId && !members.some((m) => m.userId === draft.assigneeId) && (
                <option value={draft.assigneeId}>Former member</option>
              )}
            </select>
          </label>

          <label className="field">
            <span className="field__label">LINKED CONVERSATION</span>
            <select
              className="field__control"
              value={draft.conversationId ?? ''}
              onChange={(e) => set({ conversationId: e.target.value || null })}
            >
              <option value="">None</option>
              {conversations.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.visitor.name || c.visitor.visitorId} — {c.lastMessagePreview?.slice(0, 40)}
                </option>
              ))}
              {/* The thread may be older than the 60 the list holds. */}
              {draft.conversationId && !conversations.some((c) => c.id === draft.conversationId) && (
                <option value={draft.conversationId}>Conversation (not in recent list)</option>
              )}
            </select>
          </label>
        </div>

        {draft.conversationId && (
          <Link
            className="ticket-link"
            to={notificationRoute({ appId, conversationId: draft.conversationId })}
            onClick={onClose}
          >
            <MessageCircle size={12} />
            Open the linked conversation
            {linked ? ` — ${linked.visitor.name || linked.visitor.visitorId}` : ''}
            <ExternalLink size={11} />
          </Link>
        )}

        <div className="field">
          <span className="field__label">TAGS</span>
          <div className="rail__tags">
            {draft.tags.map((tag) => (
              <span key={tag} className="chip">
                {tag}
                <button
                  type="button"
                  onClick={() => set({ tags: draft.tags.filter((t) => t !== tag) })}
                  aria-label={`Remove ${tag}`}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            <form className="chip chip--add" onSubmit={addTag}>
              <input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                placeholder="Add tag"
                aria-label="Add tag"
              />
              <button type="submit" aria-label="Add tag">
                <Plus size={10} />
              </button>
            </form>
          </div>
        </div>

        <div className="field">
          <span className="field__label">ATTACHMENTS</span>
          {/* Additive and saved on the spot, unlike the fields above: two
              agents both dropping a screenshot must not overwrite each other,
              which is why the API keeps this off the whole-ticket patch. */}
          <TicketAttachments
            appId={appId}
            ticket={ticket}
            uploads={uploads}
            onChange={onSaved}
          />
        </div>

        <div className="ticket-meta">
          <span>
            Filed {clockTime(ticket.createdAt)}
            {nameOf(ticket.createdBy) ? ` by ${nameOf(ticket.createdBy)}` : ''}
          </span>
          <span>Updated {clockTime(ticket.updatedAt)}</span>
          {closedAt && <span>Closed {clockTime(closedAt)}</span>}
        </div>

        {error && <div className="login__error">{error}</div>}

        <footer className="dialog__foot">
          <button
            type="button"
            className="button button--danger"
            onClick={remove}
            disabled={saving}
          >
            <Trash2 size={13} /> Delete
          </button>
          <span className="dialog__spacer" />
          <button type="button" className="button" onClick={onClose}>
            {dirty ? 'Discard' : 'Close'}
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={save}
            disabled={saving || !dirty || !draft.subject.trim()}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default TicketDialog;
